import React, { useEffect, useState, useCallback, useMemo } from "react";
import { apiGetJson } from "../../utils/apiClient";
import { apiDeleteJson } from "../../utils/apiClient";
import { getAuth } from "firebase/auth";

type Membership = {
    tenantId?: string;

    companyId: string;
    companyName?: string;
    shopName?: string;
    shopUrl?: string;

    role?: string | null;
    status?: string | null;
    joinedAt?: string | null;

    accountId?: string;
    accountName?: string;
};

interface UserRow {
    uid: string;
    email: string | null;
    displayName?: string | null;
    disabled?: boolean;

    creationTime?: string | null;
    lastSignInTime?: string | null;

    memberships?: Membership[];
}

// ✅ コンパクト版（余白小さめ）
const thBase =
    "border px-2 py-1 whitespace-nowrap text-[12px] leading-tight";
const tdBase =
    "border px-2 py-1 text-left align-top text-[12px] leading-tight";

function fmtDateTime(v?: string | null) {
    if (!v) return "-";
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return "-";
    return d.toLocaleString("ja-JP");
}

export default function SuperUserListPage() {
    const [users, setUsers] = useState<UserRow[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const [currentTenantId, setCurrentTenantId] = useState<string | null>(null);

    // ✅ 追加：削除中UID
    const [deletingUid, setDeletingUid] = useState<string | null>(null);

    const load = useCallback(async () => {
        setLoading(true);
        setError("");
        try {
            const data = await apiGetJson("/api/super/users");

            if (Array.isArray(data)) {
                setUsers(data as UserRow[]);
                setCurrentTenantId(null);
            } else {
                const tid = typeof data?.tenantId === "string" ? data.tenantId : null;
                const rows = Array.isArray(data?.rows) ? data.rows : [];
                setCurrentTenantId(tid);
                setUsers(rows as UserRow[]);
            }
        } catch (e: any) {
            setError(e?.message ?? "ユーザー一覧の取得に失敗しました");
            setUsers([]);
            setCurrentTenantId(null);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const hasTenantInfo = useMemo(() => {
        return users.some((u) => (u.memberships ?? []).some((m) => !!m.tenantId));
    }, [users]);

    // ✅ 追加：削除処理
    const deleteUser = useCallback(
        async (u: UserRow) => {
            const uid = u.uid;

            // 自分自身の削除は防止（保険）
            const myUid = getAuth().currentUser?.uid;
            if (myUid && myUid === uid) {
                alert("自分自身は削除できません。");
                return;
            }

            const label = u.email || u.uid;
            const ok = window.confirm(
                `ユーザーを削除します。\n\n対象: ${label}\n\n※この操作は取り消せません。続行しますか？`
            );
            if (!ok) return;

            setDeletingUid(uid);
            setError("");

            try {
                // ✅ API実行（後述のバックエンド実装）
                await apiDeleteJson(`/api/super/users/${encodeURIComponent(uid)}`);

                // ✅ 画面から即除去（最小のUX）
                setUsers((prev) => prev.filter((x) => x.uid !== uid));
            } catch (e: any) {
                setError(e?.message ?? "ユーザー削除に失敗しました");
            } finally {
                setDeletingUid(null);
            }
        },
        []
    );

    return (
        <div className="w-full max-w-none min-w-0 h-full flex flex-col gap-2 px-2 py-2 overflow-hidden">
            <div className="flex items-center justify-between gap-2">
                <h1 className="text-base font-bold">ユーザー管理（SuperAdmin）</h1>

                <div className="flex items-center gap-2">
                    {currentTenantId && (
                        <span className="text-[11px] text-gray-600">
                            tenant:
                            <span className="ml-1 font-mono px-1.5 py-0.5 border rounded bg-gray-50">
                                {currentTenantId}
                            </span>
                        </span>
                    )}

                    <button
                        type="button"
                        onClick={load}
                        disabled={loading}
                        className="text-[12px] px-2 py-1 border rounded bg-white hover:bg-gray-50 disabled:opacity-50"
                    >
                        {loading ? "読込中..." : "再読み込み"}
                    </button>
                </div>
            </div>

            {error && (
                <div className="text-[12px] text-red-600 bg-red-50 border border-red-200 rounded px-2 py-1">
                    {error}
                </div>
            )}

            <div className="flex-1 min-h-0 overflow-auto w-full bg-white border rounded">
                <table className="min-w-[1400px] w-full table-fixed border-collapse">
                    <colgroup>
                        <col style={{ width: "15rem" }} />
                        <col style={{ width: "11rem" }} />
                        <col style={{ width: "26rem" }} />
                        <col style={{ width: "6rem" }} />
                        <col style={{ width: "13rem" }} />
                        {/* ✅ 追加：操作列 */}
                        <col style={{ width: "8rem" }} />
                    </colgroup>

                    <thead className="bg-gray-100 font-semibold sticky top-0 z-10">
                        <tr>
                            <th className={thBase}>Email</th>
                            <th className={thBase}>表示名</th>
                            <th className={thBase}>
                                所属（{hasTenantInfo ? "tenant / 会社 / 店舗" : "会社 / 店舗"}）
                            </th>
                            <th className={thBase}>無効</th>
                            <th className={thBase}>最終ログイン</th>
                            {/* ✅ 追加 */}
                            <th className={thBase}>操作</th>
                        </tr>
                    </thead>

                    <tbody>
                        {loading && users.length === 0 ? (
                            <tr>
                                <td className={`${tdBase} text-gray-500`} colSpan={6}>
                                    読み込み中...
                                </td>
                            </tr>
                        ) : users.length === 0 ? (
                            <tr>
                                <td className={`${tdBase} text-gray-500`} colSpan={6}>
                                    データがありません
                                </td>
                            </tr>
                        ) : (
                            users.map((u) => {
                                const isDeleting = deletingUid === u.uid;

                                return (
                                    <tr key={u.uid} className="hover:bg-gray-50">
                                        <td className={tdBase}>
                                            <div className="truncate" title={u.email ?? ""}>
                                                {u.email ?? "-"}
                                            </div>
                                            <div
                                                className="text-[10px] text-gray-500 truncate"
                                                title={u.uid}
                                            >
                                                {u.uid}
                                            </div>
                                        </td>

                                        <td className={tdBase}>
                                            <div className="truncate" title={u.displayName ?? ""}>
                                                {u.displayName ?? ""}
                                            </div>
                                        </td>

                                        <td className={tdBase}>
                                            <div className="space-y-1">
                                                {(u.memberships ?? []).length === 0 ? (
                                                    <span className="text-gray-400">-</span>
                                                ) : (
                                                    (u.memberships ?? []).map((m, idx) => {
                                                        const k = `${m.tenantId ?? "noTenant"}__${m.companyId}__${m.shopUrl ?? "noShop"}__${idx}`;
                                                        return (
                                                            <div
                                                                key={k}
                                                                className="text-[11px] leading-tight border rounded px-1.5 py-1 bg-white"
                                                            >
                                                                {m.tenantId && (
                                                                    <div className="text-[10px] text-gray-700">
                                                                        tenant:{" "}
                                                                        <span className="font-mono">
                                                                            {m.tenantId}
                                                                        </span>
                                                                    </div>
                                                                )}

                                                                <div
                                                                    className="font-semibold truncate"
                                                                    title={m.companyName ?? ""}
                                                                >
                                                                    {m.companyName || ""}
                                                                </div>
                                                                <div
                                                                    className="font-semibold truncate"
                                                                    title={m.shopName ?? ""}
                                                                >
                                                                    {m.shopName || ""}
                                                                </div>

                                                                <div
                                                                    className="text-gray-600 truncate"
                                                                    title={m.shopUrl || ""}
                                                                >
                                                                    {m.shopUrl || ""}
                                                                </div>

                                                                <div className="text-gray-600">
                                                                    {m.role || "-"} / {m.status || "-"}
                                                                </div>
                                                            </div>
                                                        );
                                                    })
                                                )}
                                            </div>
                                        </td>

                                        <td className={`${tdBase} text-center`}>
                                            {u.disabled ? (
                                                <span className="text-red-600 font-semibold">無効</span>
                                            ) : (
                                                <span className="text-gray-500">有効</span>
                                            )}
                                        </td>

                                        <td className={tdBase}>
                                            <div className="text-[11px] whitespace-nowrap">
                                                {fmtDateTime(u.lastSignInTime)}
                                            </div>
                                        </td>

                                        {/* ✅ 追加：削除ボタン */}
                                        <td className={`${tdBase} text-center`}>
                                            <button
                                                type="button"
                                                onClick={() => deleteUser(u)}
                                                disabled={isDeleting}
                                                className={[
                                                    "text-[12px] px-2 py-1 border rounded",
                                                    "bg-white hover:bg-red-50",
                                                    "border-red-300 text-red-700",
                                                    "disabled:opacity-50 disabled:hover:bg-white",
                                                ].join(" ")}
                                            >
                                                {isDeleting ? "削除中..." : "削除"}
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}